import { CheckboxLarge } from "@components/inputs";
import {
  ChevronDownIcon,
  MapPinIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import * as Popover from "@radix-ui/react-popover";
import flatten from "lodash/flatten";
import { usePrevious, useWindowSize } from "@utils/hooks";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { joinClassNames } from "@utils/helpers";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import { useTranslations } from "next-intl";

const TOP_OFFSET = 36;

interface Props {
  regions: {
    id: number;
    order: number;
    region: string;
    cities: {
      name: string;
      regionId: number;
    }[];
  }[];
}

function formatCityList(
  regions: {
    id: number;
    order: number;
    region: string;
    cities: { name: string; regionId: number }[];
  }[],
  cities: { name: string; regionId: number }[],
  translations: ReturnType<typeof useTranslations<"GenrePage.filters">>,
  is_mobile = false
) {
  const included_regions = regions.filter(
    (region) =>
      region.cities.filter((city) => cities.includes(city)).length ===
        region.cities.length && region.cities.length > 1
  );

  const locations = [
    ...included_regions.map((region) => region.region),
    ...cities
      .filter(
        (city) =>
          !included_regions.map((region) => region.id).includes(city.regionId)
      )
      .map((city) => city.name),
  ];

  if (is_mobile ? locations.length > 1 : locations.length > 3) {
    return translations("numcities", { number: cities.length });
  }

  if (locations.length <= 2) {
    return locations.join(` ${translations("and")} `);
  }
  const last_location = `${translations("and")} ${
    locations[locations.length - 1]
  }`;
  const other_locations = locations.slice(0, -1);

  return [...other_locations, last_location].join(", ");
}

export default function CityFilter({ regions }: Props) {
  const windowSize = useWindowSize();
  const router = useRouter();
  const params = useSearchParams();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [hoveredRegion, setHoveredRegion] = useState<{
    width: number;
    height: number;
    y: number;
  } | null>(null);
  const [hoveredCity, setHoveredCity] = useState<{
    width: number;
    height: number;
    y: number;
  } | null>(null);
  const lastHoveredRegion = usePrevious(hoveredRegion).current;
  const lastHoveredCity = usePrevious(hoveredCity).current;
  const cities = params.get("cities")?.split(",") ?? [];
  const t = useTranslations("GenrePage.filters");

  const selectedCities = flatten(
    regions.map((region) =>
      region.cities.filter((city) => cities.includes(city.name))
    )
  );

  if (windowSize.width && windowSize.width <= 640) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger className="flex items-center gap-1 px-1 py-0.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-fit">
          <MapPinIcon className="h-5 w-5" />
          <span className="font-semibold">
            {selectedCities.length > 0
              ? t("eventsonincity", {
                  city: formatCityList(regions, selectedCities, t, true),
                })
              : t("filterbycity")}
          </span>
          <ChevronDownIcon
            className={joinClassNames(
              "h-5 w-5 will-change-transform transition-transform ease-in-out duration-300",
              open ? "rotate-180" : "rotate-0"
            )}
          />
        </DialogTrigger>
        <AnimatePresence>
          {open && (
            <DialogContent
              asChild
              forceMount
              handleClose={() => setOpen(false)}
              className="bottom-4 !m-2 !p-0 !rounded-3xl !bg-white !max-w-[90dvw] max-h-[65dvh] !text-indigo-800 !border-none"
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                className="flex flex-col w-full"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: 200,
                    opacity: 0,
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                  },
                }}
              >
                <div className="relative px-6 pt-6 pb-4 flex flex-col gap-3 justify-between flex-1 bg-indigo-500 rounded-t-3xl">
                  <div className="relative self-end w-8 h-8 rounded-full bg-white/10">
                    <DialogClose className="absolute top-2 left-2 w-4 h-4 text-white !font-bold">
                      <XMarkIcon />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle className="!text-white text-xl !font-bold">
                      {t("filterbycity")}
                    </DialogTitle>
                    <MapPinIcon className="absolute top-0 left-4 w-24 h-24 text-white/10 -rotate-12" />
                  </DialogHeader>
                </div>
                <div className="relative px-4 max-h-[calc(65dvh-116px)] overflow-y-auto text-black">
                  <h2 className="text-xl font-bold">{t("eventsonin")}</h2>
                  {regions.map((region) => (
                    <div
                      key={region.id}
                      className="relative z-10 flex flex-col gap-1 p-2"
                    >
                      <motion.div
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2"
                      >
                        <CheckboxLarge
                          id={region.region}
                          checked={
                            region.cities.filter((city) =>
                              selectedCities.includes(city)
                            ).length === region.cities.length
                          }
                          onCheckedChange={(checked) => {
                            const current_params = new URLSearchParams(
                              Array.from(params.entries())
                            );
                            if (checked) {
                              const new_selected_cities = [
                                ...selectedCities.map((city) => city.name),
                                ...region.cities.map((city) => city.name),
                              ];
                              current_params.set(
                                "cities",
                                new_selected_cities.join(",")
                              );
                            } else {
                              const new_selected_cities = selectedCities
                                .filter((city) => !region.cities.includes(city))
                                .map((city) => city.name);
                              if (new_selected_cities.length === 0) {
                                current_params.delete("cities");
                              } else {
                                current_params.set(
                                  "cities",
                                  new_selected_cities.join(",")
                                );
                              }
                            }
                            const query = current_params.toString();

                            router.push(
                              `${pathname}${query ? `?${query}` : ""}`
                            );
                          }}
                        />
                        <label
                          htmlFor={region.region}
                          className="font-semibold cursor-default w-full"
                        >
                          {region.region}
                        </label>
                      </motion.div>
                      <div className="flex flex-col ml-2">
                        {region.cities.map((city) => (
                          <motion.span
                            key={`${city.name}-city`}
                            whileTap={{
                              scale: 0.95,
                            }}
                            className="flex items-center gap-2"
                          >
                            <CheckboxLarge
                              id={`${city.name}-checkbox`}
                              checked={selectedCities.includes(city)}
                              onCheckedChange={(checked) => {
                                const current_params = new URLSearchParams(
                                  Array.from(params.entries())
                                );
                                if (checked) {
                                  const new_selected_cities = [
                                    ...selectedCities,
                                    city,
                                  ].map((city) => city.name);
                                  current_params.set(
                                    "cities",
                                    new_selected_cities.join(",")
                                  );
                                } else {
                                  const new_selected_cities = selectedCities
                                    .filter((c) => c.name !== city.name)
                                    .map((city) => city.name);
                                  if (new_selected_cities.length === 0) {
                                    current_params.delete("cities");
                                  } else {
                                    current_params.set(
                                      "cities",
                                      new_selected_cities.join(",")
                                    );
                                  }
                                }
                                const query = current_params.toString();
                                router.push(
                                  `${pathname}${query ? `?${query}` : ""}`
                                );
                              }}
                            />
                            <label
                              htmlFor={`${city.name}-checkbox`}
                              className="flex items-center cursor-default w-full h-10"
                            >
                              {city.name}
                            </label>
                          </motion.span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </DialogContent>
          )}
        </AnimatePresence>
      </Dialog>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className="flex items-center gap-1 px-1 py-0.5 text-gray-600 bg-white hover:bg-gray-100 border border-gray-300 rounded-md transition-colors ease-in-out duration-300 cursor-default w-fit">
        <MapPinIcon className="h-5 w-5" />
        <span className="font-semibold">
          {selectedCities.length > 0
            ? t("eventsonincity", {
                city: formatCityList(regions, selectedCities, t),
              })
            : t("filterbycity")}
        </span>
        <ChevronDownIcon
          className={joinClassNames(
            "h-5 w-5 will-change-transform transition-transform ease-in-out duration-300",
            open ? "rotate-180" : "rotate-0"
          )}
        />
      </Popover.Trigger>
      <AnimatePresence>
        {open && (
          <Popover.Portal forceMount>
            <Popover.Content
              className="relative z-40"
              sideOffset={15}
              align="end"
              alignOffset={-42}
              avoidCollisions
              forceMount
            >
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={{
                  hidden: {
                    opacity: 0,
                    y: "-1rem",
                    transition: { duration: 0.125 },
                  },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.25 }}
                className="relative flex flex-col rounded-2xl px-4 py-2 bg-white text-black ring-1 ring-inset ring-gray-100 shadow min-w-[300px] max-h-96 overflow-y-scroll"
              >
                <h2 className="text-xl font-bold">{t("eventsonin")}</h2>
                {regions.map((region, index) => (
                  <div
                    key={region.id}
                    id={region.id.toString()}
                    className="relative z-10 flex flex-col gap-1 p-2"
                    onMouseEnter={() => {
                      const height =
                        document
                          .getElementById(region.id.toString())
                          ?.getBoundingClientRect().height ?? 0;
                      const width =
                        document
                          .getElementById(region.id.toString())
                          ?.getBoundingClientRect().width ?? 0;
                      let y = TOP_OFFSET;
                      for (let i = 0; i < index; ++i) {
                        y +=
                          document
                            .getElementById(regions[i].id.toString())
                            ?.getBoundingClientRect().height ?? 0;
                      }
                      setHoveredRegion({ width, height, y });
                    }}
                    onMouseLeave={() => setHoveredRegion(null)}
                  >
                    <motion.div
                      whileTap={{ scale: 0.95 }}
                      className="flex items-center gap-2"
                    >
                      <CheckboxLarge
                        id={region.region}
                        checked={
                          region.cities.filter((city) =>
                            selectedCities.includes(city)
                          ).length === region.cities.length
                        }
                        onCheckedChange={(checked) => {
                          const current_params = new URLSearchParams(
                            Array.from(params.entries())
                          );
                          if (checked) {
                            const new_selected_cities = [
                              ...selectedCities.map((city) => city.name),
                              ...region.cities.map((city) => city.name),
                            ];
                            current_params.set(
                              "cities",
                              new_selected_cities.join(",")
                            );
                          } else {
                            const new_selected_cities = selectedCities
                              .filter((city) => !region.cities.includes(city))
                              .map((city) => city.name);
                            if (new_selected_cities.length === 0) {
                              current_params.delete("cities");
                            } else {
                              current_params.set(
                                "cities",
                                new_selected_cities.join(",")
                              );
                            }
                          }
                          const query = current_params.toString();

                          router.push(`${pathname}${query ? `?${query}` : ""}`);
                        }}
                      />
                      <label
                        htmlFor={region.region}
                        className="font-semibold cursor-default w-full"
                      >
                        {region.region}
                      </label>
                    </motion.div>
                    <div className="flex flex-col ml-2">
                      {region.cities.map((city, city_index) => (
                        <motion.span
                          key={`${city.name}-city`}
                          id={`${city.name}-city`}
                          whileTap={{
                            scale: 0.95,
                          }}
                          onMouseEnter={() => {
                            const height =
                              document
                                .getElementById(`${city.name}-city`)
                                ?.getBoundingClientRect().height ?? 0;
                            const width =
                              document
                                .getElementById(`${city.name}-city`)
                                ?.getBoundingClientRect().width ?? 0;
                            let y = TOP_OFFSET + TOP_OFFSET;
                            for (let i = 0; i < index; ++i) {
                              y +=
                                document
                                  .getElementById(regions[i].id.toString())
                                  ?.getBoundingClientRect().height ?? 0;
                            }
                            for (let i = 0; i < city_index; ++i) {
                              y +=
                                document
                                  .getElementById(
                                    `${region.cities[i].name}-city`
                                  )
                                  ?.getBoundingClientRect().height ?? 0;
                            }
                            setHoveredCity({ width, height, y });
                          }}
                          onMouseLeave={() => setHoveredCity(null)}
                          className="flex items-center gap-2"
                        >
                          <CheckboxLarge
                            id={`${city.name}-checkbox`}
                            checked={selectedCities.includes(city)}
                            onCheckedChange={(checked) => {
                              const current_params = new URLSearchParams(
                                Array.from(params.entries())
                              );
                              if (checked) {
                                const new_selected_cities = [
                                  ...selectedCities,
                                  city,
                                ].map((city) => city.name);
                                current_params.set(
                                  "cities",
                                  new_selected_cities.join(",")
                                );
                              } else {
                                const new_selected_cities = selectedCities
                                  .filter((c) => c.name !== city.name)
                                  .map((city) => city.name);
                                if (new_selected_cities.length === 0) {
                                  current_params.delete("cities");
                                } else {
                                  current_params.set(
                                    "cities",
                                    new_selected_cities.join(",")
                                  );
                                }
                              }
                              const query = current_params.toString();
                              router.push(
                                `${pathname}${query ? `?${query}` : ""}`
                              );
                            }}
                          />
                          <label
                            htmlFor={`${city.name}-checkbox`}
                            className="flex items-center cursor-default w-full h-10"
                          >
                            {city.name}
                          </label>
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
                <motion.span
                  animate={
                    hoveredCity
                      ? {
                          width: hoveredCity.width,
                          height: hoveredCity.height,
                          y: hoveredCity.y,
                          opacity: 1,
                        }
                      : hoveredRegion
                      ? {
                          width: hoveredRegion.width,
                          height: hoveredRegion.height,
                          y: hoveredRegion.y,
                          opacity: 1,
                        }
                      : lastHoveredCity
                      ? {
                          width: lastHoveredCity.width,
                          height: lastHoveredCity.height,
                          y: lastHoveredCity.y,
                          opacity: 0,
                          transition: { opacity: { duration: 0.5 } },
                        }
                      : {
                          width: lastHoveredRegion?.width,
                          height: lastHoveredRegion?.height,
                          y: lastHoveredRegion?.y ?? TOP_OFFSET,
                          opacity: 0,
                          transition: { opacity: { duration: 0.5 } },
                        }
                  }
                  transition={{ ease: "easeInOut", duration: 0.2 }}
                  className="absolute w-[calc(100%-2rem)] rounded-lg top-0 bg-gray-50 pointer-events-none"
                />
              </motion.div>
              <motion.span
                initial="hide"
                animate="show"
                exit="hide"
                variants={{
                  hide: { opacity: 0, transition: { duration: 0 } },
                  show: { opacity: 1, transition: { delay: 0.1 } },
                }}
                className="absolute z-50 top-0 inset-x-4 h-2 bg-gradient-to-b from-white"
              />
              <motion.span
                initial="hide"
                animate="show"
                exit="hide"
                variants={{
                  hide: { opacity: 0, transition: { duration: 0 } },
                  show: { opacity: 1, transition: { delay: 0.1 } },
                }}
                className="absolute z-50 bottom-0 inset-x-4 h-2 bg-gradient-to-t from-white"
              />
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
}
